/**
 * D2a Task 10: deadline engine and filing calendar.
 * Backend: app/django-core/apps/deadlines/ (urls.py, views.py, serializers.py), mounted at
 * /api/v1/deadlines/.
 *
 * - The tenant comes from the session; the client never sends one.
 * - Occurrences need `from` and `to` (YYYY-MM-DD); empty filters are dropped.
 * - Filing an occurrence sends multipart (reference, proof, note).
 * - Platform settings are IsAdminUser (is_staff) only.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';

const BASE = '/api/v1/deadlines';

/** One path segment from an id, so "../" in an id can never reach another endpoint. */
const seg = (id) => encodeURIComponent(String(id));

const compact = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  );

const filenameFromDisposition = (header) => {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || null;
};

const saveBlob = (blob, filename) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return;
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
};

export const deadlinesApi = createApi({
  reducerPath: 'deadlinesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['DeadlineSettings', 'DpoAccess', 'PlatformDeadlineSettings', 'Filing', 'Occurrence'],
  endpoints: (builder) => ({
    getDeadlineSettings: builder.query({
      query: () => ({ url: `${BASE}/settings/` }),
      providesTags: ['DeadlineSettings'],
    }),
    updateDeadlineSettings: builder.mutation({
      query: (body) => ({ url: `${BASE}/settings/`, method: 'PUT', body }),
      // naming or removing a DPO changes who may see the DSAR and breach registers
      invalidatesTags: ['DeadlineSettings', 'DpoAccess'],
    }),
    getDpoAccess: builder.query({
      query: () => ({ url: `${BASE}/dpo-access/` }),
      providesTags: ['DpoAccess'],
    }),
    getPlatformDeadlineSettings: builder.query({
      query: () => ({ url: `${BASE}/platform-settings/` }),
      providesTags: ['PlatformDeadlineSettings'],
    }),
    updatePlatformDeadlineSettings: builder.mutation({
      query: (body) => ({ url: `${BASE}/platform-settings/`, method: 'PUT', body }),
      invalidatesTags: ['PlatformDeadlineSettings'],
    }),
    getFilings: builder.query({
      query: (params = {}) => ({ url: `${BASE}/filings/`, params: compact(params) }),
      providesTags: ['Filing'],
    }),
    createFiling: builder.mutation({
      query: (body) => ({ url: `${BASE}/filings/`, method: 'POST', body }),
      invalidatesTags: ['Filing', 'Occurrence'],
    }),
    updateFiling: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${BASE}/filings/${seg(id)}/`, method: 'PATCH', body }),
      invalidatesTags: ['Filing', 'Occurrence'],
    }),
    getOccurrences: builder.query({
      query: (params = {}) => ({ url: `${BASE}/occurrences/`, params: compact(params) }),
      providesTags: ['Occurrence'],
    }),
    getOccurrence: builder.query({
      query: (id) => ({ url: `${BASE}/occurrences/${seg(id)}/` }),
      providesTags: ['Occurrence'],
    }),
    fileOccurrence: builder.mutation({
      query: ({ id, formData }) => ({
        url: `${BASE}/occurrences/${seg(id)}/file/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Filing', 'Occurrence'],
    }),
    // The proof is streamed by the tenant-scoped action (never a storage URL): fetched as a
    // blob with the auth header and saved locally.
    downloadProof: builder.mutation({
      async queryFn({ id, filename }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: `${BASE}/occurrences/${seg(id)}/proof/`,
          responseHandler: (response) => (response.ok ? response.blob() : response.json()),
        });
        if (result.error) return { error: result.error };
        const name =
          filenameFromDisposition(result.meta?.response?.headers?.get('content-disposition')) ||
          filename ||
          'proof';
        saveBlob(result.data, name);
        return { data: { filename: name } };
      },
    }),
    markNotApplicable: builder.mutation({
      query: ({ id, note }) => ({
        url: `${BASE}/occurrences/${seg(id)}/not-applicable/`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: ['Occurrence'],
    }),
  }),
});

export const {
  useGetDeadlineSettingsQuery,
  useUpdateDeadlineSettingsMutation,
  useGetDpoAccessQuery,
  useGetPlatformDeadlineSettingsQuery,
  useUpdatePlatformDeadlineSettingsMutation,
  useGetFilingsQuery,
  useCreateFilingMutation,
  useUpdateFilingMutation,
  useGetOccurrencesQuery,
  useGetOccurrenceQuery,
  useFileOccurrenceMutation,
  useDownloadProofMutation,
  useMarkNotApplicableMutation,
} = deadlinesApi;
